import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { CalendarDays, ChevronRight, Hash, Home, MoreVertical } from "lucide-react";
import client, { unwrap } from "@/api/client";
import { cn } from "@/lib/utils";
import ProjectStepper from "@/components/ProjectStepper";
import RoomsSection from "@/components/project/RoomsSection";
import DesignProposal from "@/components/project/DesignProposal";
import { EditProjectDialog, ProjectDetailsCard, QuotationsCard, formatDay } from "@/components/project/ProjectSidebar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const PROJECT_STATUS = {
  COMPLETED: { label: "Completed", pill: "bg-success/10 text-success", dot: "bg-success" },
  CLOSED: { label: "Closed", pill: "bg-secondary text-muted-foreground", dot: "bg-muted-foreground/60" },
  CANCELLED: { label: "Cancelled", pill: "bg-secondary text-muted-foreground", dot: "bg-muted-foreground/60" },
};
const IN_PROGRESS = { label: "In Progress", pill: "bg-accent/10 text-accent", dot: "bg-accent" };

function MetaChip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary/80 px-3 py-1 text-sm text-muted-foreground">
      <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
      {children}
    </span>
  );
}

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cartRef = useRef(null);
  const [cartOpen, setCartOpen] = useState(location.hash === "#project-cart");
  const [editOpen, setEditOpen] = useState(false);

  const { data: project } = useQuery({ queryKey: ["project", projectId], queryFn: () => client.get(`/projects/${projectId}`).then(unwrap) });
  const { data: customer } = useQuery({
    queryKey: ["customer", project?.customerId],
    queryFn: () => client.get(`/customers/${project.customerId}`).then(unwrap),
    enabled: !!project?.customerId,
  });
  const { data: rooms = [] } = useQuery({ queryKey: ["rooms", projectId], queryFn: () => client.get(`/rooms?projectId=${projectId}`).then(unwrap) });
  const { data: cart, isSuccess: cartLoaded } = useQuery({
    queryKey: ["cart", projectId],
    queryFn: () => client.get(`/carts?projectId=${projectId}`).then(unwrap).catch(() => ({ cart: null, items: [] })),
  });
  const { data: quotations = [] } = useQuery({ queryKey: ["quotations", projectId], queryFn: () => client.get(`/quotations?projectId=${projectId}`).then(unwrap) });

  const cartItems = useMemo(() => cart?.items || [], [cart]);

  // "View cart" from a window page links here with #project-cart; open and scroll once the cart has rendered.
  useEffect(() => {
    if (location.hash === "#project-cart" && cartLoaded) {
      setCartOpen(true);
      requestAnimationFrame(() => cartRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [location.hash, cartLoaded]);

  const generateQuotation = useMutation({
    mutationFn: (terms) => client.post("/quotations", { projectId, customerId: project.customerId, terms }),
    onSuccess: (res) => {
      toast.success("Quotation draft generated");
      navigate(`/quotations/${res.data.data._id}`);
    },
    onError: (err) => toast.error(err?.response?.data?.error?.message || "Failed to generate quotation"),
  });

  const doneUpTo = useMemo(() => {
    let d = 0;
    if (rooms.length > 0) d = 1;
    if (d === 1 && cartItems.length > 0) d = 2;
    if (d === 2 && quotations.length > 0) d = 3;
    if (d === 3 && quotations.some((q) => ["FINALIZED", "SHARED"].includes(q.status))) d = 4;
    return d;
  }, [rooms, cartItems, quotations]);

  const status = PROJECT_STATUS[project?.status] || IN_PROGRESS;

  const showCart = () => {
    setCartOpen(true);
    requestAnimationFrame(() => cartRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  const copyLink = () =>
    navigator.clipboard?.writeText(window.location.href.split("#")[0]).then(
      () => toast.success("Project link copied"),
      () => toast.error("Couldn't copy the link"),
    );

  return (
    <div data-testid="project-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="min-w-0">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
            <Link to="/projects" className="hover:text-foreground">Projects</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-foreground/80 truncate">{project?.code || project?.name || "Project"}</span>
          </nav>
          <h1 className="font-serif text-3xl sm:text-[2.6rem] leading-tight text-foreground">{project?.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
            {customer?.name && (
              <span className="text-muted-foreground">
                Customer:{" "}
                <Link to={`/customers/${customer._id}`} className="text-foreground/85 hover:text-accent">{customer.name}</Link>
              </span>
            )}
            <span className="flex flex-wrap gap-2">
              {project?.code && <MetaChip icon={Hash}>{project.code}</MetaChip>}
              {(project?.city || project?.bhk) && <MetaChip icon={Home}>{[project.bhk, project.city].filter(Boolean).join(" · ")}</MetaChip>}
              {project?.createdAt && <MetaChip icon={CalendarDays}>Created on {formatDay(project.createdAt)}</MetaChip>}
            </span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="project-more-actions" className="self-start shrink-0 bg-card">
              <MoreVertical className="h-4 w-4 mr-1.5" /> More Actions
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={() => setEditOpen(true)}>Edit project details</DropdownMenuItem>
            <DropdownMenuItem onSelect={showCart}>Open cart</DropdownMenuItem>
            {customer && <DropdownMenuItem onSelect={() => navigate(`/customers/${customer._id}`)}>View customer</DropdownMenuItem>}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={copyLink}>Copy project link</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Progress */}
      <section className="bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="project-progress">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h2 className="font-serif text-xl text-foreground">Project Progress</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Track the journey from enquiry to installation.</p>
          </div>
          <span data-testid="project-status-pill" className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap", status.pill)}>
            <span className={cn("h-2 w-2 rounded-full", status.dot)} />
            {status.label}
          </span>
        </div>
        <ProjectStepper doneUpTo={doneUpTo} />
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_22rem] gap-6 items-start">
        <div className="space-y-6 min-w-0">
          <RoomsSection projectId={projectId} rooms={rooms} cartItems={cartItems} />
          <DesignProposal
            ref={cartRef}
            projectId={projectId}
            rooms={rooms}
            items={cartItems}
            open={cartOpen}
            setOpen={setCartOpen}
            onCreateQuotation={(terms) => generateQuotation.mutate(terms)}
            creating={generateQuotation.isPending}
          />
        </div>
        <div className="space-y-6">
          <ProjectDetailsCard project={project} customer={customer} onEdit={() => setEditOpen(true)} />
          <QuotationsCard quotations={quotations} canCreate={cartItems.length > 0} onCreate={showCart} />
        </div>
      </div>

      <EditProjectDialog open={editOpen} setOpen={setEditOpen} project={project} />
    </div>
  );
}
